import { useSyncExternalStore, useCallback } from 'react';

const DARK_MODE_KEY = `bolzai_${import.meta.env.VITE_NAMESPACE}_darkMode`;

type ThemeOverride = 'dark' | 'light';

function getSystemPreference(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function migrateOldValue(): void {
  const saved = localStorage.getItem(DARK_MODE_KEY);
  if (saved === 'true') {
    localStorage.setItem(DARK_MODE_KEY, 'dark');
  } else if (saved === 'false') {
    localStorage.removeItem(DARK_MODE_KEY);
  }
}

function getSavedOverride(): ThemeOverride | null {
  const saved = localStorage.getItem(DARK_MODE_KEY);
  if (saved === 'dark' || saved === 'light') return saved;
  return null;
}

function resolveIsDark(): boolean {
  const override = getSavedOverride();
  if (override === 'dark') return true;
  if (override === 'light') return false;
  return getSystemPreference();
}

function updateDomClass(isDark: boolean): void {
  if (isDark) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

// --- External store (module scope) ---

migrateOldValue();

let currentIsDark: boolean = resolveIsDark();
updateDomClass(currentIsDark);

const listeners = new Set<() => void>();

function emitChange(): void {
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function getSnapshot(): boolean {
  return currentIsDark;
}

function setDarkMode(value: boolean): void {
  currentIsDark = value;
  localStorage.setItem(DARK_MODE_KEY, value ? 'dark' : 'light');
  updateDomClass(value);
  emitChange();
}

// Listen for OS theme changes when no user override is set
const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
mediaQuery.addEventListener('change', () => {
  const override = getSavedOverride();
  if (override === null) {
    currentIsDark = mediaQuery.matches;
    updateDomClass(currentIsDark);
    emitChange();
  }
});

// --- Hook ---

export function useDarkMode() {
  const isDarkMode = useSyncExternalStore(subscribe, getSnapshot);

  const toggleDarkMode = useCallback(() => {
    setDarkMode(!currentIsDark);
  }, []);

  return { isDarkMode, toggleDarkMode };
}
